import React, { useState, useEffect, useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { Header } from './components/Header';
import { Tabs } from './components/Tabs';
import { AugmentPanel, SkillPanel, EnchantPanel, EquipmentPanel, WikiPanel, SimulatorPanel } from './components/panels';
import './styles/App.css';
import './styles/equipment-panel.css';

const ACTIVE_TAB_KEY = 'l2_calc_active_tab';

function AppContent() {
  const { t } = useLanguage();
  const posthog = usePostHog();
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(ACTIVE_TAB_KEY);
      if (saved && ['augment', 'skill', 'enchant', 'equipment', 'wiki', 'simulator'].includes(saved)) {
        return saved;
      }
    } catch (e) {}
    return 'wiki';
  });

  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    } catch (e) {}
  }, [activeTab]);

  // Capture initial pageview with the starting tab
  useEffect(() => {
    posthog.capture('$pageview', { tab: activeTab });
  }, []);

  // Initialize Telegram WebApp if available
  useEffect(() => {
    const tg = window.Telegram?.WebApp;

    if (tg) {
      tg.ready();
      tg.expand();

      // Only request fullscreen on mobile devices (phones/tablets), not desktop
      const isMobileDevice = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        // Check for mobile/tablet user agents
        const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i;
        // Also check for touch capability as additional signal
        const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        // Check screen size (tablets are typically <= 1024px width)
        const isSmallScreen = window.innerWidth <= 1024;

        return mobileRegex.test(userAgent) && (hasTouch || isSmallScreen);
      };

      if (tg.requestFullscreen && isMobileDevice()) {
        try {
          tg.requestFullscreen();
        } catch (e) {
          // Silently fail
        }
      }
    }
  }, []);

  // Keyboard detection and scroll handling
  useEffect(() => {
    const DONE_BAR_HEIGHT = 60;
    const PADDING = 20;

    const scrollInputIntoView = (input) => {
      setTimeout(() => {
        try {
          input.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        } catch (e) {
          // Fallback to manual calculation
          const rect = input.getBoundingClientRect();
          const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
          const inputBottom = rect.bottom;
          const availableSpace = viewportHeight - DONE_BAR_HEIGHT - PADDING;
          
          if (inputBottom > availableSpace) {
            const scrollAmount = inputBottom - availableSpace;
            window.scrollBy({
              top: scrollAmount,
              behavior: 'smooth'
            });
          }
        }
      }, 300); // Wait for keyboard animation
    };

    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') {
        setKeyboardOpen(true);
        document.body.classList.add('keyboard-open');
        scrollInputIntoView(e.target);
      }
    };

    const handleFocusOut = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Small delay to handle focus moving between inputs
        setTimeout(() => {
          const activeEl = document.activeElement;
          if (activeEl.tagName !== 'INPUT' && activeEl.tagName !== 'TEXTAREA') {
            setKeyboardOpen(false);
            document.body.classList.remove('keyboard-open');
          }
        }, 100);
      }
    };

    // Visual Viewport API for better mobile keyboard handling
    let lastHeight = window.visualViewport?.height || window.innerHeight;
    
    const handleViewportResize = () => {
      if (!window.visualViewport) return;
      
      const currentHeight = window.visualViewport.height;
      const heightDiff = lastHeight - currentHeight;
      
      // If viewport got smaller (keyboard appeared) and an input is focused
      if (heightDiff > 100 && document.activeElement.tagName === 'INPUT') {
        setTimeout(() => {
          const input = document.activeElement;
          const rect = input.getBoundingClientRect();
          const viewportHeight = window.visualViewport.height;
          const inputBottom = rect.bottom;
          const availableSpace = viewportHeight - DONE_BAR_HEIGHT - PADDING;
          
          if (inputBottom > availableSpace) {
            const scrollAmount = inputBottom - availableSpace;
            window.scrollBy({
              top: scrollAmount,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
      
      lastHeight = currentHeight;
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
    };
  }, []);

  const handleDone = useCallback(() => {
    document.activeElement?.blur();
    setKeyboardOpen(false);
    document.body.classList.remove('keyboard-open');
  }, []);

  return (
    <>
      <div className="container">
        <Header />
        <AugmentPanel active={activeTab === 'augment'} />
        <SkillPanel active={activeTab === 'skill'} />
        <EnchantPanel active={activeTab === 'enchant'} />
        <EquipmentPanel active={activeTab === 'equipment'} />
        <WikiPanel active={activeTab === 'wiki'} />
        <SimulatorPanel active={activeTab === 'simulator'} />
      </div>
      <Tabs activeTab={activeTab} onTabChange={setActiveTab} />
      
      {/* Done bar for keyboard */}
      <div className={`done-bar ${keyboardOpen ? 'show' : ''}`}>
        <button onClick={handleDone}>{t('done')}</button>
      </div>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
